/**
 * The Grafik tab's one read: the trainer's hours — the whole weekly grid, and the exceptions and
 * blocks from yesterday over the 180 days one read may cover (`model/scheduleDeviations`'
 * `scheduleReadRange`) — with the zone they are stated on, and one gate over it.
 *
 * The week card and the „Inaczej niż zwykle" card both draw from it; each write reads the whole
 * booking screen again (`readBookingAgain`), this read included.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { TrainWithMeModulesBookingDtosResponsesTrainerAvailabilityResponse as TrainerAvailabilityResponse } from '~/shared/api/generated/model';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';

import { trainerAvailabilityQueryOptions } from '../api/bookingQueries';
import { scheduleReadRange } from '../model/scheduleDeviations';
import { bookingZone, wallReading, type CivilDay } from '../model/trainerClock';
import { useMinuteClock } from './useMinuteClock';

export interface ScheduleRead {
  readonly gate: SkeletonGate;
  /** `undefined` until it answers; stays at its last answer while a refresh is out. */
  readonly availability: TrainerAvailabilityResponse | undefined;
  /** The read failed with nothing to show. */
  readonly failed: boolean;
  readonly retry: () => void;
  /** Whether the trainer published a zone — the header says which one the hours are on. */
  readonly publishedZone: string | null;
  readonly zone: string;
  readonly nowMs: number;
  readonly today: CivilDay;
}

export function useScheduleRead(): ScheduleRead {
  const nowMs = useMinuteClock();
  // The range is sized on the server's default clock; a day either way changes nothing it holds.
  // At midnight the range — and so the key — moves: the last answer stays on screen while the new
  // one is read, so the tab never drops back to its wait and a week being edited is not unmounted.
  const hours = useQuery({
    ...trainerAvailabilityQueryOptions(
      scheduleReadRange(wallReading(nowMs, bookingZone(null)).day),
    ),
    placeholderData: keepPreviousData,
  });
  const gate = useSkeletonGate(hours.isPending && hours.errorUpdateCount === 0);
  const publishedZone = hours.data?.trainerTimeZoneId ?? null;
  const zone = bookingZone(publishedZone);
  return {
    gate,
    availability: hours.data,
    failed: hours.data === undefined && hours.isError,
    retry: () => {
      void hours.refetch();
    },
    publishedZone,
    zone,
    nowMs,
    today: wallReading(nowMs, zone).day,
  };
}
